/**
 * Phase 5 — Admin Architect Review Center
 * Route: /admin/architects
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCog, RefreshCw, CheckCircle2, XCircle, PauseCircle, Archive,
  ShieldCheck, ShieldX, Eye, X, Clock,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVAL_LIST = [
  'all', 'submitted', 'under_review', 'approved', 'rejected', 'suspended', 'archived', 'draft',
];
const SPECIALTIES = [
  '', 'licensed_investment_professional', 'registered_broker', 'investment_adviser',
  'portfolio_manager', 'compliance_reviewer', 'business_strategist',
  'brand_advisor', 'offer_consultant', 'market_specialist',
  'general_reviewer', 'other',
];
const STATUS_COLOR = {
  draft: theme.colors.text.muted, submitted: theme.colors.status.info,
  under_review: theme.colors.status.warning, approved: theme.colors.status.success,
  rejected: theme.colors.status.error, suspended: theme.colors.status.error,
  archived: theme.colors.text.muted,
};
const LICENSE_COLOR = {
  verified: theme.colors.status.success, rejected: theme.colors.status.error,
  pending_verification: theme.colors.status.warning, self_reported: theme.colors.text.muted,
  expired: theme.colors.status.error, unknown: theme.colors.text.muted, not_applicable: theme.colors.text.muted,
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20, boxShadow: theme.colors.shadows.sm,
};
const inputStyle = {
  padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none',
};

function Pill({ status, colorMap = STATUS_COLOR, testid }) {
  const c = colorMap[status] || theme.colors.text.muted;
  return (
    <span data-testid={testid} style={{ padding: '3px 10px', borderRadius: 999,
      background: `${c}1A`, color: c, fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>
      {(status || '').replace(/_/g, ' ')}
    </span>
  );
}

export default function AdminArchitectsReviewPage() {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filters, setFilters] = useState({ approval_status: '', specialty: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reviewForm, setReviewForm] = useState({ admin_notes: '', rejection_reason: '' });
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.approval_status) p.set('approval_status', filters.approval_status);
      if (filters.specialty) p.set('specialty', filters.specialty);
      if (filters.search) p.set('search', filters.search);
      const res = await axios.get(`${API}/admin/architects?${p.toString()}`, auth);
      setRows(res.data?.architects || []);
      setCounts(res.data?.status_counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [auth, filters]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action) => {
    if (!selected) return;
    setActing(true);
    try {
      await axios.patch(`${API}/admin/architects/${selected.id}/review`,
        { action, ...reviewForm }, auth);
      toast.success(`Architect ${action}`);
      setSelected(null); setReviewForm({ admin_notes: '', rejection_reason: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setActing(false); }
  };

  return (
    <div data-testid="admin-architects-page" style={{ padding: '24px 28px', maxWidth: 1300, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserCog size={22} color={theme.colors.primary} />
            <div style={{ fontSize: 22, fontWeight: 900 }}>Architect Review Center</div>
          </div>
          <div style={{ color: theme.colors.text.muted, fontSize: 13, marginTop: 4, maxWidth: 720 }}>
            Review Architect profiles, verify self-reported professional details, and manage Architect access.
          </div>
        </div>
        <button data-testid="admin-arch-refresh-btn" onClick={load}
          style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
            background: theme.colors.bg.card, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div style={{ ...cardStyle, marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Approval Status</label>
            <div><select data-testid="admin-arch-status-filter" style={inputStyle}
              value={filters.approval_status} onChange={e => setFilters({ ...filters, approval_status: e.target.value })}>
              <option value="">any</option>
              {APPROVAL_LIST.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Specialty</label>
            <div><select data-testid="admin-arch-specialty-filter" style={inputStyle}
              value={filters.specialty} onChange={e => setFilters({ ...filters, specialty: e.target.value })}>
              <option value="">any</option>
              {SPECIALTIES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: 11, fontWeight: 700 }}>Search</label>
            <div><input data-testid="admin-arch-search" style={{ ...inputStyle, width: '100%' }}
              value={filters.search} placeholder="name, email, company…"
              onChange={e => setFilters({ ...filters, search: e.target.value })} /></div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {Object.entries(counts).map(([k, v]) => (
            <span key={k} data-testid={`admin-arch-count-${k}`} style={{ padding: '3px 8px', borderRadius: 999,
              background: `${(STATUS_COLOR[k]) || theme.colors.text.muted}1A`,
              color: (STATUS_COLOR[k]) || theme.colors.text.muted, fontSize: 11, fontWeight: 700 }}>
              {k}: {v}
            </span>
          ))}
        </div>

        {loading ? (
          <div data-testid="admin-arch-loading" style={{ padding: 18 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="admin-arch-empty" style={{ padding: 24, textAlign: 'center', color: theme.colors.text.muted }}>
            No Architects match the current filter.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {rows.map(a => (
              <div key={a.id} data-testid={`admin-arch-row-${a.id}`}
                style={{ padding: 12, background: theme.colors.bg.secondary, borderRadius: theme.radius.sm,
                  display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 800 }}>{a.full_name}</div>
                    <Pill status={a.approval_status} testid={`admin-arch-approval-${a.approval_status}`} />
                    <Pill status={a.license_status} colorMap={LICENSE_COLOR} testid={`admin-arch-license-${a.license_status}`} />
                  </div>
                  <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
                    {a.email} · {a.company_or_firm || '—'} · {a.professional_role || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 2 }}>
                    Specialties: {(a.specialties || []).join(', ') || '—'}
                  </div>
                </div>
                <button data-testid={`admin-arch-review-${a.id}`} onClick={() => setSelected(a)}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.primary, color: '#fff', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                  <Eye size={14} /> Review
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
          <div data-testid="admin-arch-review-modal" style={{ ...cardStyle, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Review Architect</div>
              <button data-testid="admin-arch-close-review" onClick={() => setSelected(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 800 }}>{selected.full_name}</div>
              <Pill status={selected.approval_status} testid={`modal-approval-${selected.approval_status}`} />
              <Pill status={selected.license_status} colorMap={LICENSE_COLOR} testid={`modal-license-${selected.license_status}`} />
            </div>
            <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 10 }}>
              {selected.email} · {selected.professional_role || '—'} · {selected.company_or_firm || '—'}
            </div>
            <div style={{ display: 'grid', gap: 4, marginBottom: 10, fontSize: 12, color: theme.colors.text.secondary }}>
              <div><b>Specialties:</b> {(selected.specialties || []).join(', ') || '—'}</div>
              {selected.credentials_summary && <div><b>Credentials:</b> {selected.credentials_summary}</div>}
              {selected.license_details && <div><b>License details:</b> {selected.license_details}</div>}
              {selected.experience_summary && <div><b>Experience:</b> {selected.experience_summary}</div>}
              {selected.website_or_profile_link && (
                <div><b>Link:</b> <a href={selected.website_or_profile_link} target="_blank" rel="noreferrer">{selected.website_or_profile_link}</a></div>
              )}
              <div><b>Availability:</b> {selected.availability_status}</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Admin Notes</label>
              <textarea data-testid="admin-arch-notes-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={reviewForm.admin_notes} onChange={e => setReviewForm({ ...reviewForm, admin_notes: e.target.value })} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700 }}>Rejection Reason</label>
              <textarea data-testid="admin-arch-reject-input" style={{ ...inputStyle, width: '100%', minHeight: 60 }}
                value={reviewForm.rejection_reason} onChange={e => setReviewForm({ ...reviewForm, rejection_reason: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="admin-arch-action-under-review" disabled={acting} onClick={() => doAction('mark_under_review')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.warning, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Clock size={14} /> Mark Under Review
              </button>
              <button data-testid="admin-arch-action-approve" disabled={acting} onClick={() => doAction('approve')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle2 size={14} /> Approve
              </button>
              <button data-testid="admin-arch-action-reject" disabled={acting} onClick={() => doAction('reject')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.error, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <XCircle size={14} /> Reject
              </button>
              <button data-testid="admin-arch-action-suspend" disabled={acting} onClick={() => doAction('suspend')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700, color: theme.colors.status.error,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <PauseCircle size={14} /> Suspend
              </button>
              <button data-testid="admin-arch-action-archive" disabled={acting} onClick={() => doAction('archive')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Archive size={14} /> Archive
              </button>
              <button data-testid="admin-arch-action-license-verified" disabled={acting} onClick={() => doAction('mark_license_verified')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: 'none',
                  background: theme.colors.status.info, color: '#fff', cursor: 'pointer', fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ShieldCheck size={14} /> Mark License Verified
              </button>
              <button data-testid="admin-arch-action-license-rejected" disabled={acting} onClick={() => doAction('mark_license_rejected')}
                style={{ padding: '9px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700, color: theme.colors.status.error,
                  display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ShieldX size={14} /> Mark License Rejected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
